import {
  AGENT_STEP_KEYS,
  CART_DELAYS,
  stepValid,
  STORE_STEP_KEYS,
  storeSettings,
  UPSELL_LEADS,
  WIZARD_STEPS
} from '@/config/agentWizardSteps'
import { ENTHUSIASM_OPTIONS, FORMALITY_OPTIONS, TONE_OPTIONS } from '@/view/ai/agentVoiceOptions'

const ABOUT_LIMIT = 60
const SKIPPED = 'Skipped'
const ALREADY = 'Already set up'

const optionName = (options, value) => options.find(option => option.value === value)?.name ?? value
const labelFor = (options, key) => options.find(option => option.key === key)?.label ?? key
const filled = value => Boolean(value && value.trim())
const stepLabel = key => WIZARD_STEPS.find(step => step.key === key)?.label ?? key
export const agentRowKey = (stepKey, storeId) => (storeId ? `${stepKey}:${storeId}` : stepKey)
const lowerFirst = text => text.charAt(0).toLowerCase() + text.slice(1)

export const voiceLine = data => {
  const { tone, formality_level, enthusiasm_level, emoji_enabled } = data.voice
  const parts = [
    optionName(TONE_OPTIONS, tone),
    lowerFirst(optionName(FORMALITY_OPTIONS, formality_level)),
    `${lowerFirst(optionName(ENTHUSIASM_OPTIONS, enthusiasm_level))} energy`,
    emoji_enabled ? 'with emoji' : 'no emoji'
  ]
  return parts.join(', ')
}

const aboutLine = data => {
  const text = (data.about.additional_context || '').trim()
  return text ? text.slice(0, ABOUT_LIMIT) : 'Nothing added'
}

const winbackLine = ({ winback }) => {
  const base = `Lost after ${winback.lost_window_days} days, slipping ${winback.slipping_enabled ? 'on' : 'off'}`
  return winback.offer_enabled ? `${base}, with an offer` : base
}

const reviewsLine = ({ reviews }) => `After ${reviews.order_threshold} orders`

const cartsLine = (data, storeId) => {
  const settings = storeSettings(data, 'carts', storeId)
  return `${labelFor(CART_DELAYS, settings.delay)} after, ${settings.offer_enabled ? 'with an offer' : 'no offer'}`
}

const upsellLine = (data, storeId) =>
  `${labelFor(UPSELL_LEADS, storeSettings(data, 'upsell', storeId).lead)} before close`

const AGENT_LINES = {
  winback: data => winbackLine(data),
  reviews: data => reviewsLine(data),
  carts: (data, storeId) => cartsLine(data, storeId),
  upsell: (data, storeId) => upsellLine(data, storeId)
}

const agentLine = (key, data, storeId = null) => AGENT_LINES[key](data, storeId)

const stepLine = (row, data) => {
  if (row.key === 'voice') return voiceLine(data)
  if (row.key === 'about') return aboutLine(data)
  if (row.key === 'review') return ''
  if (row.storeId) return agentLine(row.key, data, row.storeId)
  if (row.skipped) return ALREADY
  if (!data[row.key].enabled) return SKIPPED
  if (STORE_STEP_KEYS.includes(row.key) && data[row.key].per_store) {
    return 'Per store'
  }
  return agentLine(row.key, data, data[row.key].store_ids?.[0] ?? null)
}

// validity of one rail row, mirroring useAgentWizard

const rowValid = (row, rows, data, ctx) => {
  if (row.storeId) return filled(data[row.key].stores[row.storeId]?.message)
  if (row.skipped) return true
  const hasChildren = rows.some(other => other.key === row.key && other.storeId)
  if (hasChildren) return !data[row.key].enabled || data[row.key].store_ids.length > 0
  return stepValid(row.key, data, ctx)
}

const sameRow = (row, current) => row.key === current.key && (row.storeId ?? null) === (current.storeId ?? null)

const agentPath = (agentIdsByRow, id) => {
  const agentId = agentIdsByRow instanceof Map ? agentIdsByRow.get(id) : agentIdsByRow?.[id]
  return agentId ? `/ai/agents/${agentId}/settings` : undefined
}

// A shared store-step row stands for the agent of its first ticked store.
const createdAgentKey = (row, data) => {
  if (row.storeId || !STORE_STEP_KEYS.includes(row.key)) return agentRowKey(row.key, row.storeId)
  return agentRowKey(row.key, data[row.key].store_ids[0])
}

export const railRows = (rows, data, current, ctx = {}, { created = false, agentIdsByRow = {} } = {}) => {
  const currentIndex = rows.findIndex(row => sameRow(row, current))
  const firstInvalid = rows.findIndex(row => !rowValid(row, rows, data, ctx))
  const lastJumpable = firstInvalid === -1 ? rows.length - 1 : firstInvalid
  return rows.map((row, index) => {
    const key = agentRowKey(row.key, row.storeId)
    let state = 'upcoming'
    if (created || index < currentIndex) state = 'done'
    else if (index === currentIndex) state = 'current'
    const canJump = !created && (state !== 'upcoming' || index <= lastJumpable)
    const summary = state === 'upcoming' ? '' : stepLine(row, data)
    const railRow = {
      key,
      stepKey: row.key,
      storeId: row.storeId ?? null,
      label: row.label,
      state,
      summary,
      value: summary,
      canJump,
      jumpable: canJump,
      indent: Boolean(row.storeId)
    }
    const to = created ? agentPath(agentIdsByRow, createdAgentKey(row, data)) : undefined
    return to ? { ...railRow, to } : railRow
  })
}

// review step

const storeName = (stores, id) => stores.find(store => String(store.id) === String(id))?.store_name ?? ''

const storeRowLabel = (stepKey, stores, id) =>
  stores.length > 1 ? `${stepLabel(stepKey)} for ${storeName(stores, id)}` : stepLabel(stepKey)

const covered = (existing, stepKey, id) => existing[stepKey].has(String(id))

const noExisting = () => ({ winback: false, reviews: false, carts: new Set(), upsell: new Set() })

const merchantWideRows = (data, existing, build) =>
  AGENT_STEP_KEYS.filter(key => !STORE_STEP_KEYS.includes(key)).flatMap(key => build(key, data[key], existing[key]))

const storeStepRows = (data, existing, build) => STORE_STEP_KEYS.flatMap(key => build(key, data[key], existing[key]))

export const setupRows = (data, stores, existing = noExisting()) => {
  const wide = merchantWideRows(data, existing, (key, step, done) =>
    step.enabled && !done
      ? [{ key, label: stepLabel(key), detail: agentLine(key, data), stepKey: key, storeId: null }]
      : []
  )
  const perStore = storeStepRows(data, existing, (key, step) =>
    !step.enabled
      ? []
      : step.store_ids
          .filter(id => !covered(existing, key, id))
          .map(id => ({
            key: agentRowKey(key, String(id)),
            label: storeRowLabel(key, stores, id),
            detail: agentLine(key, data, id),
            stepKey: key,
            storeId: String(id)
          }))
  )
  return [...wide, ...perStore].sort((a, b) => stepOrder(a.stepKey) - stepOrder(b.stepKey))
}

const stepOrder = key => WIZARD_STEPS.findIndex(step => step.key === key)

export const skippedRows = (data, stores, existing = noExisting()) => {
  const wide = merchantWideRows(data, existing, (key, step, done) => {
    if (done) return [{ key, label: stepLabel(key), reason: ALREADY }]
    return step.enabled ? [] : [{ key, label: stepLabel(key), reason: SKIPPED }]
  })
  const perStore = storeStepRows(data, existing, (key, step, done) => {
    const coveredRows = stores
      .filter(store => done.has(String(store.id)))
      .map(store => ({
        key: agentRowKey(key, String(store.id)),
        label: storeRowLabel(key, stores, store.id),
        reason: ALREADY
      }))
    const allCovered = stores.length > 0 && coveredRows.length === stores.length
    const skipped = !step.enabled && !allCovered ? [{ key, label: stepLabel(key), reason: SKIPPED }] : []
    return [...coveredRows, ...skipped]
  })
  return [...wide, ...perStore].sort((a, b) => stepOrder(a.key.split(':')[0]) - stepOrder(b.key.split(':')[0]))
}
